import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

const StickySearchParamsContext = createContext<null | URLSearchParams>(null);

/**
 * Hook to access all of the search parameters that have ever been present in the URL, accumulatively.
 */
export function useStickySearchParams() {
  const stickySearchParams = useContext(StickySearchParamsContext);
  if (!stickySearchParams) {
    throw new Error(
      'useStickySearchParams must be used within StickySearchParamsProvider',
    );
  }

  return stickySearchParams;
}

/** Parses the sticky params, and returns the parsed data if it matches the schema; otherwise, returns `null`. */
export function useParsedStickySearchParams<T extends Record<string, unknown>>(
  schema: z.ZodType<T>,
) {
  const stickySearchParams = useStickySearchParams();
  return (
    schema.safeParse(Object.fromEntries(stickySearchParams.entries())).data ??
    null
  );
}

/**
 * Merges two sets of search parameters, with the `next` parameters overriding any duplicate keys in `current`.
 */
function mergeSearchParams(current: URLSearchParams, next: URLSearchParams) {
  const merged = new URLSearchParams(current);
  for (const [key, value] of next.entries()) {
    merged.set(key, value);
  }
  return merged;
}

/**
 * Like a Remembrall from Harry Potter, but actually useful in remembering search parameters.
 * Importantly, does not work with repeated search parameters for the same key.
 */
// alternative names: Remembrall, RememberSearch, PersistentSearch
export default function StickySearchParamsProvider({
  children,
}: PropsWithChildren) {
  const [searchParams] = useSearchParams();
  const [stickySearchParams, setStickySearchParams] = useState(
    // initialize with the current search params
    mergeSearchParams(new URLSearchParams(), searchParams),
  );

  useEffect(() => {
    // merge the current search into the remembered search
    setStickySearchParams((current) =>
      mergeSearchParams(current, searchParams),
    );
  }, [searchParams]);

  return (
    <StickySearchParamsContext.Provider value={stickySearchParams}>
      {children}
    </StickySearchParamsContext.Provider>
  );
}
